import { Group } from 'src/design/atoms/Group';
import { LoadingOverlay } from 'src/design/atoms/LoadingOverlay';
import { Paper } from 'src/design/atoms/Paper';
import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import { Title } from 'src/design/atoms/Title';
import { MRT_ColumnDef } from 'mantine-react-table';
import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useJobs } from 'src/hooks/useJobs';
import {
  useTable,
  useTableState,
} from 'src/design/organisms/DataTable/useTable';
import { useTableExports } from 'src/design/organisms/useTableExports';
import { CTCLinkIndicator } from 'src/routes/Jobs/WBS/components/CTCLinkIndicator';
import {
  UnitOfMeasureType,
  WbsTreeData,
} from 'src/routes/Jobs/WBS/components/types';
import { LinkButton } from 'src/design/atoms/LinkButton';
import { convertAccountsToTreeData } from 'src/utils';
import { useWbs } from 'src/routes/Jobs/WBS/contexts/WbsContext';

interface InstallationsProps {
  jobId: number;
  jobNumber: string;
  lastUpdated: Date;
  isServiceJob?: boolean;
}

interface InstallationsRowData {
  id: string;
  name: string;
  depth: number;
  isLeaf: boolean;
  estimatedQuantity: number;
  actualQuantity: number;
  completionPercentage: number;
  estimatedHours: number;
  actualHours: number;
  completionPercentageHours: number;
  unitOfMeasure?: UnitOfMeasureType;
  node: WbsTreeData;
  subRows?: InstallationsRowData[];
}

function roundToTwo(num: number) {
  return Math.round((num + Number.EPSILON) * 100) / 100;
}

const formatValue = (value: number, unitOfMeasure: UnitOfMeasureType) => {
  if (unitOfMeasure === 'hours') {
    return `${Number(value).toFixed(0)} hours`;
  }
  return Number(value).toFixed(0);
};

const getValueColor = (value: number, type: 'percentage' | 'default') => {
  if (type === 'percentage') {
    if (value >= 90) return 'green';
    if (value >= 75) return 'yellow';
    if (value >= 50) return 'orange';
    return 'red';
  }
  return 'black';
};

const calculateAggregatedValues = (
  nodes: InstallationsRowData[],
): {
  estimatedQuantity: number;
  actualQuantity: number;
  completionPercentage: number;
  estimatedHours: number;
  actualHours: number;
  completionPercentageHours: number;
} => {
  const leafNodes = nodes.filter((node) => node.isLeaf);

  if (leafNodes.length === 0) {
    return {
      estimatedQuantity: 0,
      actualQuantity: 0,
      completionPercentage: 0,
      estimatedHours: 0,
      actualHours: 0,
      completionPercentageHours: 0,
    };
  }

  const summed = leafNodes.reduce(
    (acc, node) => {
      acc.estimatedQuantity += node.estimatedQuantity;
      acc.actualQuantity += node.actualQuantity;
      acc.estimatedHours += node.estimatedHours;
      acc.actualHours += node.actualHours;
      return acc;
    },
    {
      estimatedQuantity: 0,
      actualQuantity: 0,
      estimatedHours: 0,
      actualHours: 0,
    },
  );

  const completionPercentage =
    summed.estimatedQuantity > 0
      ? roundToTwo((summed.actualQuantity / summed.estimatedQuantity) * 100)
      : 0;

  const completionPercentageHours =
    summed.estimatedHours > 0
      ? roundToTwo((summed.actualHours / summed.estimatedHours) * 100)
      : 0;

  return {
    ...summed,
    completionPercentage,
    completionPercentageHours,
  };
};

const transformWbsToTableData = (
  nodes: WbsTreeData[],
  depth = 0,
): InstallationsRowData[] => {
  const rows: InstallationsRowData[] = [];

  nodes.forEach((node) => {
    const shouldInclude =
      node.apply || (node.children && node.children.length > 0);
    if (!shouldInclude) return;

    const isLeaf = node.apply && (!node.children || node.children.length === 0);

    if (isLeaf) {
      const estimatedQuantity = Number(node.currentQuantityEstimate);
      const actualQuantity = Number(node.currentQuantityActual);
      const estimatedHours = Number(node.currentHoursEstimate);
      const actualHours = Number(node.currentHoursActual);

      const completionPercentage =
        estimatedQuantity > 0
          ? roundToTwo((actualQuantity / estimatedQuantity) * 100)
          : 0;

      const completionPercentageHours =
        estimatedHours > 0
          ? roundToTwo((actualHours / estimatedHours) * 100)
          : 0;

      rows.push({
        id: node.id,
        name: node.name,
        depth,
        isLeaf: true,
        estimatedQuantity,
        actualQuantity,
        completionPercentage,
        estimatedHours,
        actualHours,
        completionPercentageHours,
        unitOfMeasure: node.unitOfMeasure,
        node,
      });
    } else {
      const childRows = node.children
        ? transformWbsToTableData(node.children, depth + 1)
        : [];

      const aggregated = calculateAggregatedValues(childRows);

      rows.push({
        id: node.id,
        name: node.name,
        depth,
        isLeaf: false,
        estimatedQuantity: aggregated.estimatedQuantity,
        actualQuantity: aggregated.actualQuantity,
        completionPercentage: aggregated.completionPercentage,
        estimatedHours: aggregated.estimatedHours,
        actualHours: aggregated.actualHours,
        completionPercentageHours: aggregated.completionPercentageHours,
        unitOfMeasure: node.unitOfMeasure,
        node,
        subRows: childRows,
      });
    }
  });

  return rows;
};

export const Installations: React.FC<InstallationsProps> = ({
  jobId,
  jobNumber,
  lastUpdated,
  isServiceJob = false,
}) => {
  const navigate = useNavigate();
  const { wbsData: wbsMaster, loading: wbsLoading } = useWbs();
  const { selectedJob } = useJobs();
  const [loading, setLoading] = React.useState(true);
  const [wbs, setWbs] = React.useState<WbsTreeData[]>([]);

  React.useEffect(() => {
    if (!wbsMaster) return;

    const filteredTreeData = convertAccountsToTreeData(
      wbsMaster.wbsAccount,
      (account) =>
        (account.active &&
          account.apply &&
          account.accountType === 'labor' &&
          !account.timesheetsOnly) ||
        (account.children && account.children.length > 0),
    );
    setWbs(filteredTreeData);
    setLoading(false);
  }, [wbsMaster]);

  const tableData = useMemo(() => {
    return transformWbsToTableData(wbs);
  }, [wbs]);

  const progressColumns = useMemo<MRT_ColumnDef<InstallationsRowData>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Charge Code',
        Cell: ({ row }) => (
          <Group gap="xs" align="center">
            <Text c="black">{row.original.name}</Text>
            <CTCLinkIndicator node={row.original.node} size="xs" />
          </Group>
        ),
      },
      {
        id: 'estimatedQuantity',
        size: 150,
        accessorFn: (row) => row.estimatedQuantity,
        header: 'Est Qty',
        Cell: ({ cell }) => (
          <Text c={getValueColor(cell.getValue<number>(), 'default')}>
            {formatValue(cell.getValue<number>(), '')}
          </Text>
        ),
      },
      {
        id: 'actualQuantity',
        size: 150,
        accessorFn: (row) => row.actualQuantity,
        header: 'Installed Qty',
        Cell: ({ cell }) => (
          <Text c={getValueColor(cell.getValue<number>(), 'default')}>
            {formatValue(cell.getValue<number>(), '')}
          </Text>
        ),
      },
      {
        id: 'completionPercentage',
        size: 150,
        accessorFn: (row) => row.completionPercentage,
        header: '% Comp',
        Cell: ({ cell }) => (
          <Text c={getValueColor(cell.getValue<number>(), 'percentage')}>
            {formatValue(cell.getValue<number>(), '')}%
          </Text>
        ),
      },
    ],
    [],
  );

  const hoursColumns = useMemo<MRT_ColumnDef<InstallationsRowData>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Charge Code',
        Cell: ({ row }) => (
          <Group gap="xs" align="center">
            <Text c="black">{row.original.name}</Text>
            <CTCLinkIndicator node={row.original.node} size="xs" />
          </Group>
        ),
      },
      {
        id: 'estimatedHours',
        size: 150,
        accessorFn: (row) => row.estimatedHours,
        header: 'Est Hrs',
        Cell: ({ cell }) => (
          <Text c={getValueColor(cell.getValue<number>(), 'default')}>
            {formatValue(cell.getValue<number>(), 'hours')}
          </Text>
        ),
      },
      {
        id: 'actualHours',
        size: 150,
        accessorFn: (row) => row.actualHours,
        header: 'Actual Hrs',
        Cell: ({ cell }) => (
          <Text c={getValueColor(cell.getValue<number>(), 'default')}>
            {formatValue(cell.getValue<number>(), 'hours')}
          </Text>
        ),
      },
      {
        id: 'completionPercentageHours',
        size: 150,
        accessorFn: (row) => row.completionPercentageHours,
        header: '% Comp',
        Cell: ({ cell }) => (
          <Text c={getValueColor(cell.getValue<number>(), 'percentage')}>
            {formatValue(cell.getValue<number>(), '')}%
          </Text>
        ),
      },
    ],
    [],
  );

  const progressTableState = useTableState({
    identifier: 'installations-progress-table',
    columns: progressColumns,
  });

  const hoursTableState = useTableState({
    identifier: 'installations-hours-table',
    columns: hoursColumns,
  });

  const progressExports = useTableExports<InstallationsRowData>(
    'installations-progress',
    (data) => {
      const flattenData = (
        items: InstallationsRowData[],
        prefix = '',
      ): any[] => {
        return items.flatMap((item) => {
          const current = {
            ['Job Name']: selectedJob?.name,
            ['Job Number']: selectedJob?.jobNumber,
            ['Name']: prefix + item.name,
            ['Estimated Quantity']: item.estimatedQuantity,
            ['Actual Quantity']: item.actualQuantity,
            ['Completion %']: item.completionPercentage,
          };
          if (item.subRows && item.subRows.length > 0) {
            return [current, ...flattenData(item.subRows, prefix + '  ')];
          }
          return [current];
        });
      };
      return flattenData(data);
    },
  );

  const { table: progressTable } = useTable({
    ...progressTableState,
    tableOptions: {
      enableExpanding: true,
      enableExpandAll: true,
      enableBottomToolbar: false,
      enableTopToolbar: false,
      enableColumnActions: false,
      enableColumnFilters: false,
      enablePagination: false,
      columns: progressColumns,
      data: tableData,
      manualPagination: false,
      manualSorting: false,
      manualFiltering: false,
      enableRowActions: false,
      enableClickToCopy: false,
      initialState: {
        expanded: {},
      },
      mantineTableProps: {
        withRowBorders: true,
      },
    },
    getRowId: (row) => row.id,
    totalCount: tableData.length,
    isLoading: loading,
    isFetching: false,
    isError: false,
    ...progressExports,
  });

  const hoursExports = useTableExports<InstallationsRowData>(
    'installations-hours',
    (data) => {
      const flattenData = (
        items: InstallationsRowData[],
        prefix = '',
      ): any[] => {
        return items.flatMap((item) => {
          const current = {
            ['Job Name']: selectedJob?.name,
            ['Job Number']: selectedJob?.jobNumber,
            ['Name']: prefix + item.name,
            ['Estimated Hours']: item.estimatedHours,
            ['Actual Hours']: item.actualHours,
            ['Completion %']: item.completionPercentageHours,
          };
          if (item.subRows && item.subRows.length > 0) {
            return [current, ...flattenData(item.subRows, prefix + '  ')];
          }
          return [current];
        });
      };
      return flattenData(data);
    },
  );

  const { table: hoursTable } = useTable({
    ...hoursTableState,
    tableOptions: {
      enableExpanding: true,
      enableExpandAll: true,
      enableBottomToolbar: false,
      enableTopToolbar: false,
      enableColumnActions: false,
      enableColumnFilters: false,
      enablePagination: false,
      columns: hoursColumns,
      data: tableData,
      manualPagination: false,
      manualSorting: false,
      manualFiltering: false,
      enableRowActions: false,
      enableClickToCopy: false,
      initialState: {
        expanded: {},
      },
      mantineTableProps: {
        withRowBorders: true,
      },
    },
    getRowId: (row) => row.id,
    totalCount: tableData.length,
    isLoading: loading,
    isFetching: false,
    isError: false,
    ...hoursExports,
  });

  return (
    <Paper pos="relative">
      <LoadingOverlay visible={wbsLoading || loading} />
      <Stack gap="md">
        <Group justify="space-between">
          <Title order={4}>Estimated vs Actual</Title>
          <LinkButton size="compact-xs" to={`/jobs/${jobId}/wbs`}>
            Open
          </LinkButton>
        </Group>

        {wbs.length > 0 && (
          <>
            <Title order={5}>Progress</Title>
            {progressTable}

            <Title order={5}>Hours</Title>
            {hoursTable}
          </>
        )}
        {wbs.length === 0 && !wbsLoading && (
          <>
            <Text ta="center" py="xl">
              WBS not configured or no applicable charge codes found
            </Text>
          </>
        )}
      </Stack>
    </Paper>
  );
};
